import React, { useEffect, useMemo, useState } from 'react';
import SettingContainer from '../SettingContainer';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';
import { Skeleton, SkeletonPill } from '../../../util/skeleton/Skeleton';
import { formatDate } from '../../../util/araknerdlib';

/* ---------------- Updated Helpers ---------------- */
const chipForValue = (val) => {
  const v = String(val ?? '').trim().toUpperCase();
  const isYes = v === 'YES' || v === 'ON' || v === 'TRUE' || v === '1';
  const isNo  = v === 'NO'  || v === 'OFF' || v === 'FALSE' || v === '0';
  
  if (isYes) return <span className="badge-soft-success">Yes</span>;
  if (isNo)  return <span className="badge-soft-secondary">No</span>;
  return null;
};

const prettyValue = (val) => chipForValue(val) || <span className="font-weight-bold text-indigo">{String(val ?? '—')}</span>;

const toArray = (prefs) => {
  // Accepts either the new keyed-object or legacy array and returns a clean array
  if (!prefs) return [];
  if (Array.isArray(prefs)) return prefs;
  if (typeof prefs === 'object') return Object.values(prefs);
  return [];
};

/* ---------------- Aesthetic Card Refinement ---------------- */
const PreferenceCard = ({ item, onEdit, highlight = '' }) => {
  const hi = highlight.toLowerCase();
  const name = item.preference_name || item.preference_key;
  const value = item.preference_value;

  return (
    <div
      className="permission-card" // Matches your Permissions/Special Accounts style
      style={{ minHeight: '160px', display: 'flex', flexDirection: 'column' }}
    >
      <div className="d-flex justify-content-between align-items-start w-100 flex-wrap" style={{ gap: '8px' }}>
        <div className="d-flex flex-grow-1 mr-2" style={{ minWidth: '0' }}>
          <div className="icon-box mr-3 flex-shrink-0" style={{ 
              width: 42, height: 42, borderRadius: '10px', 
              background: '#F1F5F9', color: '#475569',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <i className="fa-solid fa-sliders" />
          </div>
          <div style={{ minWidth: '0' }}>
            <span className="text-truncate" style={{ 
                display: 'block', fontWeight: '700', fontSize: '13px', 
                color: '#1E293B', letterSpacing: '0.02em'
            }}>
              {hi ? name.split(new RegExp(`(${hi})`, 'ig')).map((p, i) =>
                    p.toLowerCase() === hi ? <mark key={i} className="p-0 bg-warning-light">{p}</mark> : <span key={i}>{p}</span>
                  ) : name}
            </span>
            <div className="mt-2">{prettyValue(value)}</div>
          </div>
        </div>
      </div>

      <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
        <span className="text-muted" style={{ fontSize: '11px' }}>
          Updated: {formatDate(item.updated_at)}
        </span>
        <button
          className="btn btn-modern-outline py-1 px-3"
          style={{ fontSize: '12px' }}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(item);
          }}
        >
          <i className="fa-solid fa-pen-to-square mr-1" /> Edit
        </button>
      </div>
    </div>
  );
};
/* ---------------- skeletons ---------------- */
const PrefSkeletonGrid = () => (
  <div className="row">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={i}>
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <span className="rounded d-inline-flex align-items-center justify-content-center mr-3" style={{ width: 44, height: 44, background: '#EEF2F6' }}>
                <i className="fa fa-sliders-h text-muted" />
              </span>
              <div style={{ width: '70%' }}>
                <Skeleton w="80%" h={14} />
                <Skeleton w="60%" h={12} style={{ marginTop: 6 }} />
              </div>
            </div>
            <div className="mt-3">
              <SkeletonPill w={100} h={26} />
            </div>
            <div className="mt-3 d-flex justify-content-between">
              <Skeleton w="140px" h={12} />
              <Skeleton w="90px" h={28} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ---------------- toolbar ---------------- */
const Toolbar = ({ query, setQuery, onRefresh }) => (
  <div className="mb-4 mt-2 px-1 d-flex justify-content-between align-items-center">
    <div className="input-group" style={{ 
        borderRadius: '10px', overflow: 'hidden', border: '1px solid #E2E8F0',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)', maxWidth: '400px'
    }}>
      <span className="input-group-text bg-white border-0 text-muted">
        <i className="fa fa-search"></i>
      </span>
      <input
        type="text"
        className="form-control border-0 shadow-none"
        style={{ padding: '10px 10px', fontSize: '14px' }}
        placeholder="Search preferences..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
    </div>

    <button className="btn btn-modern-outline ml-2" onClick={onRefresh}>
        <i className="fa-solid fa-rotate mr-1" /> Refresh
    </button>
  </div>
);

/* ---------------- main ---------------- */
const PreferenceHome = () => {
  const { openSettings } = useTheme();
  const { getPreferences, preferences, loadingPreferences, errorPreferences } = useConfig();
  const [query, setQuery] = useState('');

  useEffect(() => {
    getPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (pref) => openSettings('Edit Preference', 'ConfigEditPreference', pref);
  const handleRefresh = () => getPreferences(true);

  // 🔁 Adopt NEW keyed-object format (with graceful fallback)
  const list = useMemo(() => {
    const arr = toArray(preferences);
    // sort nicely: default_currency, show_letter_header, letter_head_format, show_signatories, then alphabetically
    const weight = (k = '') =>
      k === 'default_currency' ? 0 :
      k === 'show_letter_header' ? 1 :
      k === 'letter_head_format' ? 2 :
      k === 'show_signatories' ? 3 : 99;

    return arr.sort((a, b) => {
      const wa = weight(a.preference_key);
      const wb = weight(b.preference_key);
      return wa !== wb ? wa - wb : (a.preference_name || '').localeCompare(b.preference_name || '');
    });
  }, [preferences]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      (p.preference_name || '').toLowerCase().includes(q) ||
      (p.preference_value || '').toLowerCase().includes(q) ||
      (p.preference_key || '').toLowerCase().includes(q)
    );
  }, [list, query]);

  return (
    <SettingContainer title="Preferences" icon="fa fa-wrench">
      <Toolbar query={query} setQuery={setQuery} onRefresh={handleRefresh} />

      {errorPreferences && <Notification type="danger" message={String(errorPreferences)} />}

      {loadingPreferences && !preferences ? (
        <PrefSkeletonGrid />
      ) : list.length === 0 ? (
        <div className="text-center p-5 border rounded">
          <div className="mb-2">
            <span className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light" style={{ width: 64, height: 64 }}>
              <i className="fa fa-wrench text-muted" />
            </span>
          </div>
          <div className="h6">No preferences found</div>
          <div className="text-muted">Preferences let you tailor report headers, signature visibility, and defaults.</div>
          <button className="btn btn-light btn-sm mt-2" onClick={handleRefresh}>
            <i className="fa fa-sync mr-1" />
            Refresh
          </button>
        </div>
      ) : (
        <div className="row">
          {filtered.map((pref) => (
            <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={pref.id}>
              <PreferenceCard item={pref} onEdit={handleEdit} highlight={query} />
            </div>
          ))}
        </div>
      )}
    </SettingContainer>
  );
};

export default PreferenceHome;
